import { gql } from '@apollo/client';
import { AdminLayout } from 'components/admin/admin-layout';
import { Button } from 'components/buttons/button';
import {
  BookSelectorValue,
  BooksSelector,
} from 'components/forms/book-selector';
import {
  CategoriesSelector,
  CategorySelectorValue,
} from 'components/forms/category-selector';
import { TextEditor } from 'components/text-editor/text-editor';
import { convertToRaw, EditorState } from 'draft-js';
import { useInput } from 'lib/hooks/use-input';
import { IPostType, postTypes } from 'lib/types';
import { useSavePostMutation } from 'lib/graphql-generated';
import {
  BooksellerSelect,
  BooksellerSelectorValue,
  DefaultBooksellerSelectorValue,
} from 'components/forms/bookseller-select';
import {
  createEmptyState,
  saveDraftContent,
} from 'components/text-editor/editor-utils';
import { useRouter } from 'next/router';
import slugify from 'slugify';
import {
  inputClassNames,
  labelClassNames,
  StandardInput,
} from 'components/forms/input';

const SAVE_POST = gql`
  mutation SavePost($data: posts_insert_input!) {
    insert_posts_one(object: $data) {
      slug
    }
  }
`;

export const NewPost = () => {
  const router = useRouter();
  const title = useInput<string>('');
  const author = useInput<BooksellerSelectorValue>(
    DefaultBooksellerSelectorValue
  );
  const type = useInput<IPostType['name']>('article');
  const image = useInput<string>('');
  const categories = useInput<CategorySelectorValue[]>([]);
  const books = useInput<BookSelectorValue[]>([]);
  const content = useInput<EditorState>(createEmptyState());
  const [save, { data, loading, error }] = useSavePostMutation();

  const isSaveDisabled =
    !(title.isModified && author.isModified && content.isModified) || loading;

  const handleSave = (e) => {
    e.preventDefault();
    save({
      variables: {
        data: {
          author_id: author.value.id,
          title: title.value.trim(),
          slug: slugify(title.value, { lower: true, strict: true }),
          type: type.value,
          image: image.value.trim(),
          post_books: {
            data: books.value.map((b, index) => ({
              book_isbn: b.data.isbn,
              rank_order: index,
            })),
          },
          category_posts: {
            data: categories.value.map((c) => ({ category_name: c.data.name })),
          },
          content: saveDraftContent(content.value),
        },
      },
    }).then(({ data }) =>
      router.push(`/admin/posts/${data.insert_posts_one.slug}`)
    );
  };

  return (
    <>
      <AdminLayout page='New Post'>
        <article className='space-y-4'>
          <header className='flex flex-row justify-between'>
            <h1 className='header text-4xl'>Creating New Post</h1>
            <section className='space-x-4'>
              <Button
                buttonType='primary'
                buttonSize='md'
                disabled={isSaveDisabled}
                onClick={handleSave}>
                Save
              </Button>
              <Button buttonType='ghost' buttonSize='md' href='/admin/posts'>
                Cancel
              </Button>
            </section>
          </header>
          <form className='flex flex-row flex-wrap w-full space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-4 xl:gap-8'>
            <StandardInput
              type='text'
              label='Title'
              name='title'
              id='title'
              bind={{ ...title.bind }}
            />

            <label className={`w-full flex flex-col ${labelClassNames}`}>
              Type
              <select
                className={`${inputClassNames} capitalize`}
                onChange={(e) => type.setValue(e.target.value)}
                value={type.value}>
                {postTypes.map((type) => (
                  <option key={type.name} value={type.name}>
                    {type.name}
                  </option>
                ))}
              </select>
            </label>

            <StandardInput
              type='url'
              label='Image'
              name='image'
              id='image'
              bind={{ ...image.bind }}
            />

            <BooksSelector
              value={books.value}
              setValue={books.setValue}
              label={`Linked Books (${books.value.length})`}
              order
              className='w-full flex flex-col z-20'
            />

            <CategoriesSelector
              value={categories.value}
              setValue={categories.setValue}
              label='Categories'
              className='w-full flex flex-col z-20'
            />

            <BooksellerSelect
              label='Author'
              value={author.value}
              setValue={author.setValue}
              className='w-full flex flex-col z-20'
            />

            <label
              htmlFor='content'
              className='w-full flex flex-col col-span-full'>
              Content
              <TextEditor state={content.value} setState={content.setValue} />
            </label>
          </form>
        </article>
      </AdminLayout>
    </>
  );
};

export default NewPost;
